/**
 * API Client Hook
 * Provides authenticated API calls to the Permission Service with JWT Bearer tokens.
 * Uses the centralized useTokenManager for token lifecycle.
 */
import { useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";

import { useDevBypass } from "../utils/devBypass";
import { useTokenManager } from "./useTokenManager";

import { getConfig } from "../config";



interface ApiError {
  code: string;
  message: string;
}

interface ApiErrorResponse {
  error: ApiError;
}

export class ApiClientError extends Error {
  public code: string;
  public status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "ApiClientError";
    this.code = code;
    this.status = status;
  }
}

interface ApiClient {
  get: <T>(path: string) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  delete: <T>(path: string) => Promise<T>;
}

export function useApiClient(): ApiClient {
  const { instance } = useMsal();
  const tokenManager = useTokenManager();

  const buildHeaders = useCallback(
    async (forceRefresh = false): Promise<HeadersInit> => {
      const token = await tokenManager.getToken(forceRefresh);
      const { userId, email, roles } = tokenManager.getUserInfo();

      return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "X-User-Id": userId,
        "X-User-Email": email,
        "X-User-Roles": roles.join(","),
      };
    },
    [tokenManager],
  );

  const fetchWithAuth = useCallback(
    async <T>(path: string, options: RequestInit = {}): Promise<T> => {
      const headers = await buildHeaders();
      const apiBase = getConfig().permissionApiUrl || "/api";
      const url = `${apiBase}${path}`;
      const response = await fetch(url, { ...options, headers });

      if (response.status === 401 && !useDevBypass) {
        // Force-refresh the token and retry once
        const retryHeaders = await buildHeaders(true);
        const retryResponse = await fetch(url, { ...options, headers: retryHeaders });

        if (retryResponse.status === 401) {
          // Refresh token is also expired — force interactive login
          try {
            await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] });
          } catch {
            // Redirect navigates away
          }
          throw new ApiClientError("Session expired. Redirecting to login...", "SESSION_EXPIRED", 401);
        }

        if (!retryResponse.ok) {
          let errorData: ApiErrorResponse | null = null;
          try { errorData = await retryResponse.json(); } catch {}
          const message = errorData?.error?.message || `HTTP ${retryResponse.status}: ${retryResponse.statusText}`;
          throw new ApiClientError(message, errorData?.error?.code || "UNKNOWN_ERROR", retryResponse.status);
        }

        if (retryResponse.status === 204) return {} as T;
        return retryResponse.json();
      }

      if (!response.ok) {
        let errorData: ApiErrorResponse | null = null;
        try { errorData = await response.json(); } catch {}
        const message = errorData?.error?.message || `HTTP ${response.status}: ${response.statusText}`;
        throw new ApiClientError(message, errorData?.error?.code || "UNKNOWN_ERROR", response.status);
      }

      if (response.status === 204) return {} as T;
      return response.json();
    },
    [buildHeaders, instance],
  );

  return useMemo<ApiClient>(
    () => ({
      get: <T>(path: string) => fetchWithAuth<T>(path, { method: "GET" }),
      post: <T>(path: string, body?: unknown) =>
        fetchWithAuth<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
      put: <T>(path: string, body?: unknown) =>
        fetchWithAuth<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
      patch: <T>(path: string, body?: unknown) =>
        fetchWithAuth<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
      delete: <T>(path: string) => fetchWithAuth<T>(path, { method: "DELETE" }),
    }),
    [fetchWithAuth],
  );
}

export default useApiClient;
